import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './AviosCurrencySymbol.module.css';

const aviosCurrencySymbol = cva(styles['avios-currency-symbol'], {
  variants: {
    styleVariant: {
      primary: styles['avios-currency-symbol--primary'],
      secondary: styles['avios-currency-symbol--secondary'],
      white: styles['avios-currency-symbol--white'],
      onSpend: styles['avios-currency-symbol--on-spend'],
      onCollect: styles['avios-currency-symbol--on-collect'],
      accentPrimary: styles['avios-currency-symbol--accent-primary'],
      accentSecondary: styles['avios-currency-symbol--accent-secondary'],
    },
  },
  defaultVariants: {
    styleVariant: 'primary',
  },
});

export type AviosCurrencySymbolVariants = VariantProps<
  typeof aviosCurrencySymbol
>;

export type AviosCurrencySymbolKind = 'collect' | 'spend' | 'balance';
export type AviosCurrencySymbolStyleVariant =
  | 'primary'
  | 'secondary'
  | 'white'
  | 'onSpend'
  | 'onCollect'
  | 'accentPrimary'
  | 'accentSecondary';

export interface AviosCurrencySymbolProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'fill' | 'className' | 'style'> {
  /**
   * The style variant to use for the Avios currency symbol
   * @default 'primary'
   */
  styleVariant?: AviosCurrencySymbolStyleVariant;
  /**
   * The kind of Avios currency to display "collect", "spend", or "balance"
   * @default 'collect'
   */
  kind?: AviosCurrencySymbolKind;
  /**
   * The screen reader label to use for the SVG element
   * @default "Avios ${kind} currency symbol"
   * @deprecated Use `aria-label` instead
   */
  label?: string;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

const AviosCurrencyKindPath = ({
  kind = 'collect',
}: Pick<AviosCurrencySymbolProps, 'kind'>) => {
  switch (kind) {
    case 'collect':
      return (
        <path
          fill="currentColor"
          d="M7.562 11.188a.317.317 0 0 0 .317-.318V8.648h2.222a.317.317 0 0 0 .317-.317V7.06a.317.317 0 0 0-.317-.317H7.879V4.522a.317.317 0 0 0-.317-.318h-1.27a.317.317 0 0 0-.317.318v2.222H3.752a.317.317 0 0 0-.317.317v1.27c0 .175.142.317.317.317h2.223v2.222c0 .175.142.318.317.318z"
        />
      );
    case 'spend':
      return (
        <path
          fill="currentColor"
          d="M9.288 4.914a.3.3 0 0 1 .056.005.27.27 0 0 1 .217.268v4.371a.273.273 0 0 1-.273.273H8.195a.273.273 0 0 1-.273-.273V7.712l-3.265 3.265a.273.273 0 0 1-.386 0l-.773-.773a.273.273 0 0 1 0-.386l3.265-3.265H4.917a.273.273 0 0 1-.274-.273V5.187c0-.151.123-.273.274-.273z"
        />
      );
    case 'balance':
      return null;
  }
};

const DEFAULT_DIMENSIONS = {
  width: '24px',
  height: '24px',
};

export const AviosCurrencySymbol = ({
  kind = 'collect',
  styleVariant = 'accentPrimary',
  width,
  height,
  'data-testid': dataTestId,
  label,
  ...props
}: AviosCurrencySymbolProps) => {
  // Handle custom dimensions from props
  const dimensions = React.useMemo(() => {
    if (width && height) {
      return {
        width,
        height,
      };
    }

    if (width) {
      return {
        width,
        height: width,
      };
    }

    if (height) {
      return {
        width: height,
        height: height,
      };
    }

    return DEFAULT_DIMENSIONS;
  }, [width, height]);

  // TODO: Remove deprecated `label` prop in future major release
  const screenReaderLabel = label || props['aria-label'];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0.82 0.52 14.00 14.02"
      className={aviosCurrencySymbol({ styleVariant })}
      aria-label={screenReaderLabel}
      aria-hidden={props['aria-hidden'] ?? true}
      role={props['aria-hidden'] ? undefined : 'img'}
      data-testid={dataTestId}
      {...dimensions}
      {...props}
    >
      <path
        fill="#0032A0"
        d="M14.512 5.968C14.025 5.127 9.659.865 4.364.526c-.923-.06-1.797.448-2.18 1.262-1.01 2.17-2.246 7.022-.459 11.442a2.06 2.06 0 0 0 1.94 1.312c.098 0 .191-.005.29-.016 6.043-.744 9.923-4.946 10.579-6.306.246-.514.508-1.3-.033-2.24z"
      ></path>
      <AviosCurrencyKindPath kind={kind} />
    </svg>
  );
};

AviosCurrencySymbol.displayName = 'AviosCurrencySymbol';
export default AviosCurrencySymbol;
